import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

dayjs.extend(customParseFormat);

export function TimePickerV2({
  value,
  onChange,
  disabled,
  className,
  dropdownSide = "bottom",
}: {
  value?: string;
  onChange?: (time: string) => void;
  disabled?: boolean;
  className?: string;
  dropdownSide?: "top" | "bottom";
}) {
  const [hour, setHour] = useState("--");
  const [minute, setMinute] = useState("--");
  const [ampm, setAmpm] = useState<"AM" | "PM">("AM");
  const [openField, setOpenField] = useState<"hour" | "minute" | "ampm" | null>(
    null
  );

  const hours = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0")
  );
  const ampmOptions: ("AM" | "PM")[] = ["AM", "PM"];

  const handleChange = (h = hour, m = minute, a = ampm) => {
    // Chỉ thực thi nếu hour và minute hợp lệ (không rỗng, không NaN)
    if (!h || !m || isNaN(Number(h)) || isNaN(Number(m))) return;

    let hr = parseInt(h, 10);
    if (a === "PM" && hr < 12) hr += 12;
    if (a === "AM" && hr === 12) hr = 0;
    const result = `${String(hr).padStart(2, "0")}:${m}:00`;
    onChange?.(result);
  };

  const dropdownPositionClass =
    dropdownSide === "top"
      ? "bottom-full mb-1 top-auto"
      : "top-full mt-1 bottom-auto";

  useEffect(() => {
    if (!value) {
      setHour("--");
      setMinute("--");
      setAmpm("AM");
      onChange?.("");
      return;
    }

    const parsed = dayjs(value, "HH:mm:ss", true);
    if (!parsed.isValid()) return;

    let hr = parsed.hour();
    const min = parsed.minute();

    // xác định AM/PM và giờ 12h
    const ampmVal = hr >= 12 ? "PM" : "AM";
    hr = hr % 12 || 12;

    setHour(String(hr).padStart(2, "0"));
    setMinute(String(min).padStart(2, "0"));
    setAmpm(ampmVal);
  }, [onChange, value]);

  return (
    <div className={`flex justify-start items-center relative border border-[#E2E8F0] rounded-xl bg-white w-full h-[40px] px-3 gap-2 dark:border-gray-600 dark:bg-gray-800 text-sm text-[#020817] dark:text-gray-100 ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-900' : ''} ${className || ''}`}>
      {/* Clock icon */}
      <Clock className={`w-4 h-4 flex-shrink-0 ${disabled ? 'text-gray-400' : 'text-[#90A1B9]'}`} />
      {/* Hour input */}
      <div className="relative">
        <input
          value={hour}
          disabled={disabled}
          onFocus={() => setOpenField("hour")}
          onChange={(e) => {
            let v = e.target.value.replace(/\D/g, "").slice(0, 2);

            if (v) {
              if (Number(v) < 0) v = "01";
              else if (Number(v) > 12) v = "12";
            }

            setHour(v);
            if (v && Number(v) >= 1 && Number(v) <= 12) handleChange(v);
          }}
          onBlur={(e) => {
            let v = e.target.value.replace(/\D/g, "").slice(0, 2);
            setTimeout(() => setOpenField(null), 150)
            if (Number(v) < 10) v = v.padStart(2, "0");
            if (Number(v) === 0) v = "12";
            setHour(v);
          }}
          className={`w-4 text-center border-none rounded-md dark:bg-transparent focus:outline-none ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-[#020817] dark:text-gray-100'}`}
          placeholder="--"
        />
        {openField === "hour" && (
          <div
            className={`absolute left-[-12px] z-10 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-md shadow-md max-h-20 overflow-y-auto scrollbar-hide ${dropdownPositionClass}`}
          >
            {hours.map((h) => (
              <div
                key={h}
                onMouseDown={() => {
                  setHour(h);
                  handleChange(h);
                  setOpenField(null);
                }}
                className="px-3 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-center dark:text-gray-100"
              >
                {h}
              </div>
            ))}
          </div>
        )}
      </div>

      <span className={`px-1 ${disabled ? 'text-gray-400' : 'text-[#020817] dark:text-gray-100'}`}>:</span>

      {/* Minute input */}
      <div className="relative">
        <input
          value={minute}
          disabled={disabled}
          onFocus={() => setOpenField("minute")}
          onChange={(e) => {
            let v = e.target.value.replace(/\D/g, "").slice(0, 2);

            if (v) {
              if (Number(v) < 0) v = "00";
              if (Number(v) > 59) v = "59";
            }

            setMinute(v);
            if (v && Number(v) >= 0 && Number(v) < 60) handleChange(undefined, v);
          }}
          onBlur={(e) => {
            let v = e.target.value.replace(/\D/g, "").slice(0, 2);
            setTimeout(() => setOpenField(null), 150);
            if (Number(v) < 10) v = v.padStart(2, "0");
            setMinute(v);
          }}
          className={`w-4 text-center border-none rounded-md dark:bg-transparent focus:outline-none ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-[#020817] dark:text-gray-100'}`}
          placeholder="--"
        />
        {openField === "minute" && (
          <div
            className={`absolute left-[-12px] z-10 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-md shadow-md max-h-20 overflow-y-auto scrollbar-hide ${dropdownPositionClass}`}
          >
            {minutes.map((m) => (
              <div
                key={m}
                onMouseDown={() => {
                  setMinute(m);
                  handleChange(undefined, m);
                  setOpenField(null);
                }}
                className="px-3 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-center dark:text-gray-100"
              >
                {m}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AM/PM custom dropdown */}
      <div className="relative ml-2">
        <div
          className={`w-4 text-center border-none rounded-md py-1 dark:bg-transparent ${disabled ? "text-gray-400 cursor-not-allowed" : "text-[#020817] dark:text-gray-100 cursor-pointer"
            }`}
          onClick={() =>
            !disabled && setOpenField(openField === "ampm" ? null : "ampm")
          }
        >
          {ampm}
        </div>
        {openField === "ampm" && (
          <div
            className={`w-12 absolute left-[-12px] right-0 z-10 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-md shadow-md ${dropdownPositionClass}`}
          >
            {ampmOptions.map((opt) => (
              <div
                key={opt}
                onMouseDown={() => {
                  setAmpm(opt);
                  handleChange(undefined, undefined, opt);
                  setOpenField(null);
                }}
                className="px-3 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-center dark:text-gray-100"
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
